<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { LOADER_SCREEN_WAVES } from "#/components/ksoCheckout/stages";
import { useDecorationStore } from "#/store/decoration";
import { useThemeStore } from "#/store/theme";
import CartCompleteCardSuccess from "#/ui/cartComplete/completeCardSuccess/CartCompleteCardSuccess.vue";
import CartCompleteCash from "#/ui/cartComplete/completeCash/CompleteCash.vue";
import CompletedQRCode from "#/ui/cartComplete/completedQRCode/CompletedQRCode.vue";
import CartCompleteFiscal from "#/ui/cartComplete/completeFiscal/CartCompleteFiscal.vue";
import CartCompleteKsoPrint from "#/ui/cartComplete/completeKsoPrint/CartCompleteKsoPrint.vue";
import CartCompletePaymentTracker from "#/ui/cartComplete/completePaymentTracker/CartCompletePaymentTracker.vue";
import CartCompleteYandexBadge from "#/ui/cartComplete/completeYandexBadge/CartCompleteYandexBadge.vue";
import { useCartStore } from "../../store/cart";
import CartCompleteKioskSuccess from "../../ui/cartComplete/completeKioskSuccess/CartCompleteKioskSuccess.vue";
import MarkedItemsNotice from "../markedItemsNotice/MarkedItemsNotice.vue";

const props = defineProps<{ orderId?: string; isKiosk?: boolean; decoration?: string }>();
const emit = defineEmits<{ "cancel-payment": [] }>();

const storeCart = useCartStore();
const themeStore = useThemeStore();
const decor = useDecorationStore().screen(props.decoration);

const { orderCompleted_lib, markedItemsSnapshot } = storeToRefs(storeCart);

const isKsoPrintScreen = computed(() => themeStore.data?.loader_screen?.type === LOADER_SCREEN_WAVES && storeCart.configPayment?.context === "kiosk");

const shownState = ref(orderCompleted_lib.value);
const isMarkedNoticeVisible = ref(false);
watch(orderCompleted_lib, (newVal, oldVal) => {
	if (newVal) shownState.value = newVal;

	if (!oldVal && newVal) {
		const text = themeStore.data?.kiosk_ask_staff_to_scan_mark_text;

		if (text && markedItemsSnapshot.value.length > 0) {
			isMarkedNoticeVisible.value = true;
		}
	} else if (!newVal) {
		isMarkedNoticeVisible.value = false;
		storeCart.clearMarkedItemsSnapshot();
	}
});

const onAcceptMarkedNotice = () => {
	isMarkedNoticeVisible.value = false;
	storeCart.clearMarkedItemsSnapshot();
};
</script>

<template>
	<Transition name="fade">
		<section class="wrapper_qr" v-if="orderCompleted_lib" v-bind="decor.bind('completed', { paid: shownState === 3 })">
			<MarkedItemsNotice
				v-if="isMarkedNoticeVisible && (orderCompleted_lib === 3 || orderCompleted_lib === 7)"
				:text="themeStore.data?.kiosk_ask_staff_to_scan_mark_text || ''"
				:items="markedItemsSnapshot"
				@accept="onAcceptMarkedNotice"
			/>

			<Transition name="fade">
				<CartCompleteKsoPrint v-if="orderCompleted_lib === 1 && isKsoPrintScreen" />
				<CartCompleteFiscal v-else-if="orderCompleted_lib === 1" :is-kiosk="props.isKiosk" />
				<CartCompleteCardSuccess v-else-if="orderCompleted_lib === 2" />
				<CartCompleteKioskSuccess v-else-if="orderCompleted_lib === 3" :is-kiosk="props.isKiosk" :decoration="props.decoration" />
				<CompletedQRCode v-else-if="orderCompleted_lib === 4" :is-kiosk="props.isKiosk" />
				<CartCompleteCash v-else-if="orderCompleted_lib === 7" />
				<CartCompleteYandexBadge v-else-if="orderCompleted_lib === 8" @cancel="emit('cancel-payment')" />
				<CartCompletePaymentTracker v-else-if="orderCompleted_lib === 9" :order-id="props.orderId" @cancel="emit('cancel-payment')" />
			</Transition>
		</section>
	</Transition>
</template>

<style lang="scss" scoped>
  @use './cartCompletedOrder.scss';
</style>
